import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchConditionsAndSlotsAsync,
  getCheckoutProfileIdAsync,
  getSubscriptionDetailsAsync,
  loginAsync,
  selectAppState,
  setActivePage,
  setIFrameLink,
  setIsAppBridgePresent,
  setIsEligibleForReview,
  setLoading,
  setPlatformStoreId,
  setUnleashReady,
  toggleHelpModal,
  toggleUnsavedBarValue,
  updateBreadcrumbValue,
  updateSideBarSections,
} from "../app/slice/appSlice";
import { PageNames, StoreInfo } from "../app/slice/appSlice.model";
import {
  createConditionAsync,
  selectCondition,
  selectConditionAsync,
  setSelectedVariantId,
} from "../app/slice/conditionSlice";
import { APP_PLATFORM, CRISP_WEBSITE_ID, SHOPIFY_API_KEY } from "../config";
import { Platform, PlatformType } from "../models";
import { isCrispVisible, setupCrispChat, showCrispChat } from "../utils/crisp";
import { useTranslation } from "react-i18next";
import { DashboardVersion2Pages, PrePurchaseLinkItems, PostPurchaseLinkItems } from "../components/Sidebar/Sidebar";
import { LoggerContext } from "@reconvert/react-logger";
import { generateBreadcrumbFormat, IFramePostMessageEvent, IFramePostMessageType, sendPostMessage } from "../utils/helpers";
import { useFlowBuilderSession, initI18nUiComponent } from "@reconvert/react-ui-component";
import { PageType } from "@reconvert/reconvert-utils";
import { initializeBanners, setSlotStatuses } from "../app/slice/bannerSlice";
import i18n from "../utils/i18n";
import { initI18n } from "@reconvert/reconvert-utils";
import { useUnleashClient, useUnleashContext,useFlagsStatus } from "@unleash/proxy-client-react";
import useUnleashFlag from "./useUnleashFlag";
import { getShopInfoThunk, updateShopInfoLocal, updateShopInfoThunk } from "../app/slice/shopinfoSlice";
import { RECONVERT_IO_HELP_URL } from "../config";
import { selectShopInfoState } from "../ReviewProviderWrapper";

// Setting up the Crisp client once storeClient data is loaded
const useCrispSetup = (queryParams: URLSearchParams, storeInfo?: StoreInfo): void => {
  const crispVisible = isCrispVisible();
  const isAdmin = queryParams.get("isAdmin");

  useEffect(() => {
    if (!isAdmin && storeInfo && storeInfo.storeClientId) {
      setupCrispChat(
        CRISP_WEBSITE_ID,
        APP_PLATFORM === Platform.Wix ? String(storeInfo.storeClientId) : btoa(`${(storeInfo.storeClientId ?? 1) * 6}`),
        storeInfo.email,
        storeInfo.shopOwner,
        { siteName: storeInfo.shopName, url: storeInfo.storeUrl },
        {
          storeName: storeInfo?.shopName,
          storeUrl: storeInfo?.storeUrl,
          is_legacy: storeInfo?.dashboardVersion === 1,
          is_shopify_plus: storeInfo?.isShopifyPlus,
        },
      );
    }

    if (CRISP_WEBSITE_ID && !crispVisible && storeInfo) {
      showCrispChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crispVisible, storeInfo]);
};


const useAppBridge = (queryParams: URLSearchParams, dispatch: any): void => {
  // If shopify key is present and isAdmin is not present in query params, that means app bridge is present and app is rendering from Shopify
  if (SHOPIFY_API_KEY && !queryParams.get("isAdmin")) {
    dispatch(setIsAppBridgePresent(true));
  }
};

// Fetching conditions and slots for current active page or refetch it when active page changes
const useLoadConditionsOnActivePageChange = (
  dispatch: any,
  activePage?: PageType,
  selectedConditionId?: string,
  selectedVariantId?: string,
): void => {
  const platformStoreId = sessionStorage.getItem("platformStoreId");
  const { getSelectedConditionFromSession, getSelectedVariantFromSession } = useFlowBuilderSession();
  const { t } = useTranslation();

  useEffect(() => {
    if (activePage) {
      dispatch(selectCondition(null));
      dispatch(fetchConditionsAndSlotsAsync())
        .unwrap()
        .then(({ conditions: conditionsRes, slots }: { conditions: any; slots: any }) => {
          if (conditionsRes?.success) {
            if (!conditionsRes.data?.length) {
              const defaultConditionData = {
                name: "Default condition",
                isDefault: 1,
                location: activePage,
              };

              dispatch(createConditionAsync(defaultConditionData))
                .unwrap()
                .then((response: any) => {
                  dispatch(selectCondition(response.data.id));
                  dispatch(selectConditionAsync(response.data.id));
                });
              return;
            }

            const defaultConditionId =
              APP_PLATFORM === Platform.Wix
                ? getSelectedConditionFromSession() ||
                  selectedConditionId ||
                  conditionsRes.data[conditionsRes.data.length - 1].id
                : selectedConditionId || conditionsRes.data[conditionsRes.data.length - 1].id;

            dispatch(selectCondition(defaultConditionId));
            dispatch(selectConditionAsync(defaultConditionId))
              .unwrap()
              .then((res: any) => {
                dispatch(
                  updateBreadcrumbValue({
                    breadCrumb: generateBreadcrumbFormat({ page: t(res?.data?.location as string), condition: t(res?.data?.name as string) }),
                  }),
                );
                if (APP_PLATFORM === Platform.Shopify) {
                  selectedVariantId && dispatch(setSelectedVariantId(selectedVariantId));
                }

                if (APP_PLATFORM === Platform.Wix) {
                  getSelectedVariantFromSession() && dispatch(setSelectedVariantId(getSelectedVariantFromSession()));
                }
              });
          }
        });
    }
  }, [dispatch, activePage, platformStoreId]);
};

// Removing query parameters from the URL when the app is unloaded
const useRemoveQueryParams = (queryParams: URLSearchParams): void => {
  const { loading } = useAppSelector(selectAppState);
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!loading) {
      queryParams.delete("token");
      queryParams.delete("platformStoreId");
      queryParams.delete("redirectUrl");
      queryParams.delete("page");
      queryParams.delete("isFromMigrationWizard");
      queryParams.delete("canAccessCheckout");
      queryParams.delete("openHelpModal");
      queryParams.delete("conditionId");
      queryParams.delete("variantId");
      setSearchParams(queryParams);
    }
  }, [loading]);
};

const useConfigUnleash = (): void => {
  const { platformStoreId } = useAppSelector(selectAppState);
  const client = useUnleashClient();
  const updateContext = useUnleashContext();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Only start client when platformStoreId is available
    if (!platformStoreId) {
      // Reset ready state if platformStoreId is not available
      dispatch(setUnleashReady(false));
      return;
    }

    // Listen for the 'ready' event to know when flags are loaded
    const handleReady = () => {
      console.log("Unleash client ready");
      dispatch(setUnleashReady(true));
    };

    // Listen for 'error' event in case client fails to start
    const handleError = () => {
      console.error("Unleash client failed to start");
      dispatch(setUnleashReady(false));
    };

    const startClient = async () => {
      // Set up listeners before starting
      client.on("ready", handleReady);
      client.on("error", handleError);

      try {
        updateContext({ properties: { platformStoreId: platformStoreId } });
        await client.start();
      } catch (error) {
        console.error("Error starting Unleash client:", error);
        dispatch(setUnleashReady(false));
      }
    };

    startClient();

    // Cleanup listeners
    return () => {
      client.off("ready", handleReady);
      client.off("error", handleError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformStoreId, dispatch]);
};

const useAppInit = (): void => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = queryParams.get("token") || "";
  const paramPlatformStoreId = queryParams.get("platformStoreId") || sessionStorage.getItem("platformStoreId") || "";

  queryParams.get("platformStoreId") && sessionStorage.setItem("platformStoreId", paramPlatformStoreId);

  const redirectUrl = queryParams.get("redirectUrl");
  const page = queryParams.get("page");
  const isFromMigrationWizard = queryParams.get("isFromMigrationWizard");
  const canAccessCheckout = queryParams.get("canAccessCheckout");
  const openHelpModal = queryParams.get("openHelpModal");
  const isAdmin = queryParams.get("isAdmin");
  const selectedConditionId = queryParams.get("conditionId") || "";
  const selectedVariantId = queryParams.get("variantId") || "";
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { logger } = useContext(LoggerContext);
  const { setPageInSession, getPageFromSession } = useFlowBuilderSession();
  const flowEditorUrlFromSession = sessionStorage.getItem("flow_editor_url");
  const { hasAccessOfUnleashFlag } = useUnleashFlag();
  const { flagsReady } = useFlagsStatus();
  const { platformStoreId, storeInfo, activePage, sideBarSections, isUnleashReady } = useAppSelector(selectAppState);
  const { shopInfo } = useAppSelector(selectShopInfoState);

  useCrispSetup(queryParams, storeInfo);
  useAppBridge(queryParams, dispatch);

  useEffect(() => {
    if (flowEditorUrlFromSession && APP_PLATFORM === Platform.Wix) {
      dispatch(setIFrameLink(flowEditorUrlFromSession));
    }
  }, [dispatch, flowEditorUrlFromSession]);

  const handleHelpVideoShow = () => {
    const link =
      APP_PLATFORM === Platform.Shopify
        ? `${RECONVERT_IO_HELP_URL}/en/article/how-to-use-the-flow-builder-120kpr5/reader/?bust=1706874298491`
        : `${RECONVERT_IO_HELP_URL}/en/article/reconvert-flow-builder-for-wix-1q3qudk/reader/?bust=1716296560710`;

    dispatch(
      toggleHelpModal({
        link: link,
        title: t("How to use the Flow Builder").toString(),
      }),
    );
  };

  const handleSave = () => {
    console.log("debug handle save : ");
  };

  const handleCloseQuickSlotSetupModal = async () => {
    try {
      sendPostMessage(IFramePostMessageType.SYNC_SLOT_STATUS);
    } catch (error) {
      console.error("Error syncing slot status:", error);
    }
  };

  const handleCloseHelpModal = useCallback(() => {
    dispatch(toggleHelpModal());
    if (shopInfo?.show_help_video_modal === "0") {
      dispatch(
        updateShopInfoLocal({
          show_help_video_modal: "1",
        }),
      );
      dispatch(updateShopInfoThunk({
        show_help_video_modal: "1",
      }));
    }
  }, [shopInfo]);

  const handleCloseHelpModalRef = useRef(handleCloseHelpModal);

  useEffect(() => {
    handleCloseHelpModalRef.current = handleCloseHelpModal;
  }, [handleCloseHelpModal]);

  useEffect(() => {
    dispatch(getShopInfoThunk());
    const handleMessage = async (event: any) => {
      if (event?.data?.page !== "flow-builder") return false;

      switch (event.data.event) {
        case IFramePostMessageEvent.HELP_VIDEO_SHOW:
          handleHelpVideoShow();
          break;
        case IFramePostMessageEvent.HANDLE_SAVE:
          handleSave();
          break;
        case IFramePostMessageEvent.CLOSE_QUICK_SLOT_SETUP_MODAL:
          handleCloseQuickSlotSetupModal();
          break;
        case IFramePostMessageEvent.CLOSE_HELP_MODAL:
          sendPostMessage(IFramePostMessageType.CLOSE_HELP_MODAL);
          handleCloseHelpModalRef.current();
          break;
        case IFramePostMessageEvent.SYNC_SLOT_STATUS_RESPONSE:
          dispatch(setSlotStatuses(event.data.slotStatuses as any[]));
          break;
        case IFramePostMessageEvent.SET_IS_ELIGIBLE_FOR_REVIEW_RESPONSE:
          dispatch(setIsEligibleForReview(event.data.isEligibleForReview as boolean));
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sessionStorage.setItem("platformStoreId", paramPlatformStoreId);
    dispatch(setPlatformStoreId(paramPlatformStoreId));
  }, [dispatch, paramPlatformStoreId, token]);

  useEffect(() => {
    if (redirectUrl) {
      sessionStorage.setItem("redirectUrl", redirectUrl || "");
    }
  }, [redirectUrl]);

  useEffect(() => {
    dispatch(loginAsync(token))
      .unwrap()
      .then(async () => {
        dispatch(initializeBanners());
        if (APP_PLATFORM === Platform.Shopify) {
          if(isAdmin){
            dispatch(setIsEligibleForReview(false));
            return;
          }

          sendPostMessage(IFramePostMessageType.SYNC_SLOT_STATUS);
          sendPostMessage(IFramePostMessageType.SET_IS_ELIGIBLE_FOR_REVIEW);
        }
      });
  }, [dispatch, token]);

  useEffect(() => {
    if (storeInfo) {
      const language = storeInfo?.appLanguage || "en";


      if (APP_PLATFORM === Platform.Shopify) {
        dispatch(getCheckoutProfileIdAsync());
        dispatch(getSubscriptionDetailsAsync());
      }

      i18n.changeLanguage(language);
      initI18n(language);
      initI18nUiComponent(language);
    }
  }, [dispatch, storeInfo]);

  const getDefaultPage = useCallback(() => {
    let firstPage;

    if (storeInfo && !activePage) {
      if (page) {
        firstPage = page as PageType;
        const item = [...PrePurchaseLinkItems, ...PostPurchaseLinkItems].find(a => a.type.toString() === page);

        if (item) {
          const isActiveDashboardV2Page =
            DashboardVersion2Pages.includes(item.type) && storeInfo?.dashboardVersion === 2;

          if (item.unleashFlag && hasAccessOfUnleashFlag(item.unleashFlag!)) {
            return firstPage;
          }
        }
      }

      if (APP_PLATFORM! === Platform.Wix) {
        firstPage = PageType.CHECKOUT;
      }

      return firstPage;
    }
  }, [activePage, page, storeInfo, hasAccessOfUnleashFlag]);

  useEffect(() => {
    // Wait for Unleash client to be ready before checking flags
    // This prevents race condition where flags are checked before client has loaded them
    if (storeInfo && !activePage && isUnleashReady && flagsReady) {
      //Sidebar Section
      let filterSideBarSections = sideBarSections
        .filter(a => a.enabled)
        .map(c => {
          return {
            ...c,
            links: c.links.filter(l => {
              if (!l.allowedPlatform.includes(APP_PLATFORM!)) return false;

              if (APP_PLATFORM === Platform.Wix && l.type === PageType.CHECKOUT) return true;

              // Product page is available on Shopify without unleash flag check
              // The unleash flag is only for Wix
              if (APP_PLATFORM === Platform.Shopify && l.type === PageType.PRE_PURCHASE_PRODUCT_PAGE) return true;

              if (l.unleashFlag) {
                const hasAccess = hasAccessOfUnleashFlag(l.unleashFlag!);

                if (hasAccess && DashboardVersion2Pages.includes(l.type)) {
                  if (isFromMigrationWizard === "true") {
                    return true;
                  }

                  return storeInfo?.dashboardVersion === 2;
                }

                if (hasAccess && l.type === PageType.CHECKOUT) {
                  return storeInfo?.checkoutVersion === 2;
                }

                return hasAccess;
              }

              return true;
            }),
          };
        });

      if (canAccessCheckout) {
        filterSideBarSections = filterSideBarSections.map(d => {
          return {
            ...d,
            links: d.links.map(l => {
              return {
                ...l,
                disabled: l.type === PageType.CHECKOUT ? canAccessCheckout !== "true" : false,
                toolTip:
                  l.type === PageType.CHECKOUT && canAccessCheckout !== "true"
                    ? t("Limited to Stores on the Shopify Plus Plan")
                    : undefined,
              };
            }),
          };
        });
      }

      if (openHelpModal) {
        dispatch(
          toggleHelpModal({
            link: `${RECONVERT_IO_HELP_URL}/en/article/how-to-use-the-flow-builder-120kpr5/reader/?bust=1706874298491`,
            title: t("How to use the Flow Builder").toString(),
          }),
        );
      }

      logger.log(`FB Sidebar Page Value Debug :: ${platformStoreId}`, {
        filterSideBarSections: filterSideBarSections,
        canAccessCheckout: canAccessCheckout,
        isFromMigrationWizard: isFromMigrationWizard,
      });

      dispatch(updateSideBarSections(filterSideBarSections));
      let firstPage = getDefaultPage();

      if (firstPage && !filterSideBarSections.some(a => a.links.some(l => l.type === firstPage))) {
        firstPage =
          filterSideBarSections[0].links.length > 0 ? filterSideBarSections[0].links[0].type : PageType.THANK_YOU_PAGE;
      }

      if (!firstPage) {
        firstPage = getPageFromSession() || filterSideBarSections?.[0]?.links?.[0]?.type || PageType.THANK_YOU_PAGE;
      }

      setPageInSession(firstPage);
      dispatch(setActivePage(firstPage));

      // Hide loader once storeInfo is loaded and activePage is set
      dispatch(setLoading(false));
    }
  }, [
    storeInfo,
    activePage,
    dispatch,
    page,
    sideBarSections,
    isFromMigrationWizard,
    getDefaultPage,
    canAccessCheckout,
    t,
    logger,
    platformStoreId,
    isUnleashReady,
    hasAccessOfUnleashFlag,
    flagsReady
  ]);

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify && !isAdmin) {
      dispatch(toggleUnsavedBarValue(true));
    }
  }, [dispatch, isAdmin]);

  useLoadConditionsOnActivePageChange(dispatch, activePage, selectedConditionId, selectedVariantId);
  useConfigUnleash();
  useRemoveQueryParams(queryParams);
};

export default useAppInit;
